/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import _ from 'lodash';
import { IFeedSpread } from '@tradingblock/types';
import { useStateSelector, blocksSelectors } from '../global/dataSelectors';
import { useFeedActions } from './useFeedActions';
import { useSubscribedSymbolsContext } from './context/DataFeedSymbolsContext';
import { useFeatureFlag } from '@tradingblock/components';
import { useQuotesFeedStatus, useSpreadsFeedStatus } from './FeedSelectors';
import { useDispatcher } from '../global/hooks';
import { QuoteDataActions } from '../global/actions';
import { getFeedSubscription, spreadEquals, calculateQuoteChanges, calculateSpreadChanges } from './utils/subscriptionHelpers';
import { useInitialSubscriptionHandler, useIncrementalUpdateHandler } from './hooks/useSubscriptionHandlers';
import { useDebouncedSubscription } from './hooks/useDebouncedSubscription';

export const DataFeedInitializer: React.FC<{ apiToken: string; accountId: number; authToken: string }> = ({
  apiToken,
  accountId,
}) => {
  // ============================================================================
  // Context & State
  // ============================================================================
  const {
    subscribedQuotes,
    setSubscribedQuotes,
    subscribedSpreads,
    setSubscribedSpreads,
  } = useSubscribedSymbolsContext();

  const { subscribeQuote, unsubscribeQuote, subscribeOrderUpdates } = useFeedActions(apiToken);
  const { dispatch } = useDispatcher();

  // ============================================================================
  // Data Selectors
  // ============================================================================
  const blockQuotes = useStateSelector(blocksSelectors.allBlockQuotes);
  const bannerSymbols = useStateSelector((state) => {
    const ui = state.settings.ui;
    const symbols = ui && ui.bannerSymbols;
    return Array.isArray(symbols) ? symbols : [];
  });
  const quotes = useMemo(() => _.uniq([...blockQuotes, ...bannerSymbols]).sort(), [blockQuotes, bannerSymbols]);
  const spreads = useStateSelector(blocksSelectors.allBlockSpreads);

  // ============================================================================
  // Feed Status & Feature Flags
  // ============================================================================
  const feedProviderStatus = useStateSelector((s) => s.feedProvider.status);
  const quotesFeedStatus = useQuotesFeedStatus();
  const spreadsFeedStatus = useSpreadsFeedStatus();
  const { isEnabled: useLofiOrderUpdates } = useFeatureFlag('lofi-order-updates');
  const { isEnabled: useNewQuoteService } = useFeatureFlag('new_quote_service');
  const { isEnabled: useNewSpreadService } = useFeatureFlag('new_spread_service');

  const effectiveFeedStatus = useNewQuoteService ? quotesFeedStatus : feedProviderStatus;
  const effectiveSpreadsFeedStatus = useNewSpreadService ? spreadsFeedStatus : feedProviderStatus;
  // Check if feed is open - either new websocket OR old websocket must be open
  // This ensures initial subscriptions can happen even if new websocket isn't ready yet
  // If new service is enabled but not open, allow subscriptions if old websocket is open
  const isFeedOpen = effectiveFeedStatus === 'open' || (useNewQuoteService && quotesFeedStatus !== 'open' && feedProviderStatus === 'open');
  const isSpreadsFeedOpen = effectiveSpreadsFeedStatus === 'open' || (useNewSpreadService && spreadsFeedStatus !== 'open' && feedProviderStatus === 'open');

  // ============================================================================
  // Subscription Handlers
  // ============================================================================
  const handleInitialSubscription = useInitialSubscriptionHandler(accountId, subscribeQuote);
  const handleIncrementalUpdates = useIncrementalUpdateHandler(accountId, subscribeQuote, unsubscribeQuote);

  // ============================================================================
  // Change Calculations
  // ============================================================================
  const quotesChanges = useMemo(() => {
    const changes = calculateQuoteChanges(quotes, subscribedQuotes);
    return changes;
  }, [quotes, subscribedQuotes, blockQuotes]);

  // Create stable key for quotesChanges to prevent unnecessary debounce resets
  // Format: "toAdd:SYMBOL1,SYMBOL2|toRemove:SYMBOL3" or "toAdd:SYMBOL1|" or "|toRemove:SYMBOL2"
  const quotesChangesKey = useMemo(() => {
    const toAddKey = quotesChanges.toAdd.length > 0 ? `toAdd:${quotesChanges.toAdd.sort().join(',')}` : '';
    const toRemoveKey = quotesChanges.toRemove.length > 0 ? `toRemove:${quotesChanges.toRemove.sort().join(',')}` : '';
    const parts = [toAddKey, toRemoveKey].filter(Boolean);
    return parts.length > 0 ? parts.join('|') : '';
  }, [quotesChanges]);

  const spreadsChanges = useMemo(
    () => {
      const changes = calculateSpreadChanges(spreads, subscribedSpreads, spreadEquals);
      return changes;
    },
    [spreads, subscribedSpreads]
  );

  // Create stable key for spreadsChanges to prevent unnecessary debounce resets
  const spreadsChangesKey = useMemo(() => {
    const toAddKey = spreadsChanges.toAdd.length > 0
      ? `toAdd:${spreadsChanges.toAdd.map(s => {
        const spreadId = (s as any).id;
        return s.name || spreadId || JSON.stringify(s);
      }).sort().join(',')}`
      : '';
    const toRemoveKey = spreadsChanges.toRemove.length > 0
      ? `toRemove:${spreadsChanges.toRemove.map(s => {
        const spreadId = (s as any).id;
        return s.name || spreadId || JSON.stringify(s);
      }).sort().join(',')}`
      : '';
    const parts = [toAddKey, toRemoveKey].filter(Boolean);
    return parts.length > 0 ? parts.join('|') : '';
  }, [spreadsChanges]);

  // ============================================================================
  // Initialization State Tracking
  // ============================================================================
  // Use state instead of ref so useDebouncedSubscription can react to changes
  const [hasInitialized, setHasInitialized] = useState(false);
  const hasSpreadsInitializedRef = useRef(false);
  const prevFeedOpenRef = useRef(isFeedOpen);
  const prevSpreadsFeedOpenRef = useRef(isSpreadsFeedOpen);


  // ============================================================================
  // HTTP Quote Requests (independent of websocket)
  // ============================================================================
  const prevQuotesForRequestRef = useRef<string[]>([]);

  useEffect(() => {
    const quotesArray: string[] = Array.isArray(quotes) ? quotes : [];

    if (quotesArray.length > 0) {
      const newQuotes = _.difference(quotesArray, prevQuotesForRequestRef.current);
      if (newQuotes.length > 0) {
        dispatch(QuoteDataActions.requestQuote({ symbol: newQuotes }));
        prevQuotesForRequestRef.current = quotesArray;
      }
    } else if (quotesArray.length === 0 && prevQuotesForRequestRef.current.length > 0) {
      prevQuotesForRequestRef.current = [];
    }
  }, [quotes, dispatch]);

  // ============================================================================
  // Initial Quote Subscription (when feed opens)
  // ============================================================================
  useEffect(() => {
    const feedJustOpened = isFeedOpen && !prevFeedOpenRef.current;
    prevFeedOpenRef.current = isFeedOpen;

    if (feedJustOpened && !useLofiOrderUpdates) {
      subscribeOrderUpdates(accountId);
    }

    const quotesArray: string[] = Array.isArray(quotes) ? quotes : [];

    // If feed is already open but not initialized, initialize it now
    if (isFeedOpen && !hasInitialized && quotesArray.length === 0) {
      setHasInitialized(true);
      setSubscribedQuotes([]);
    }

    // Create a ref-like object that updates state when modified
    const hasInitializedRefLike = {
      current: hasInitialized,
      setCurrent: (value: boolean) => setHasInitialized(value)
    };

    handleInitialSubscription<string>(
      feedJustOpened,
      hasInitializedRefLike as any,
      quotesArray,
      getFeedSubscription({ quotes: quotesArray }),
      setSubscribedQuotes,
      'quotes'
    );
  }, [isFeedOpen, useLofiOrderUpdates, accountId, subscribeOrderUpdates, handleInitialSubscription, quotes, setSubscribedQuotes, hasInitialized]);

  // ============================================================================
  // Initial Spread Subscription (when feed opens)
  // ============================================================================
  useEffect(() => {
    const spreadsFeedJustOpened = isSpreadsFeedOpen && !prevSpreadsFeedOpenRef.current;
    prevSpreadsFeedOpenRef.current = isSpreadsFeedOpen;

    const spreadsArray: IFeedSpread[] = Array.isArray(spreads) ? spreads : [];
    handleInitialSubscription<IFeedSpread>(
      spreadsFeedJustOpened,
      hasSpreadsInitializedRef,
      spreadsArray,
      { kind: 'Spread', spreadDefinition: spreadsArray },
      setSubscribedSpreads,
      'spreads'
    );
  }, [isSpreadsFeedOpen, handleInitialSubscription, spreads, setSubscribedSpreads]);

  // ============================================================================
  // Incremental Quote Updates (debounced)
  // ============================================================================
  // Use refs to track latest values so handler always uses current state
  const quotesChangesRef = useRef(quotesChanges);
  const subscribedQuotesRef = useRef(subscribedQuotes);
  const quotesRef = useRef(quotes);
  const isFeedOpenRef = useRef(isFeedOpen);
  const capturedQuotesChangesRef = useRef<{ toAdd: string[]; toRemove: string[] } | null>(null);

  // Keep refs up to date
  quotesChangesRef.current = quotesChanges;
  subscribedQuotesRef.current = subscribedQuotes;
  quotesRef.current = quotes;
  isFeedOpenRef.current = isFeedOpen;

  // Create handler that uses captured changes (set when debounce timer starts)
  const quotesHandler = useCallback(() => {
    // Get latest values at handler time
    const latestQuotes = quotesRef.current || [];
    const latestSubscribedQuotes = subscribedQuotesRef.current || [];
    const latestIsFeedOpen = isFeedOpenRef.current;
    const latestHasInitialized = hasInitialized;

    // Recalculate changes at handler time to ensure consistency
    // This ensures changes are based on current state, not stale captured state
    const currentChanges = calculateQuoteChanges(latestQuotes, latestSubscribedQuotes);

    // Use captured changes if they're more recent, otherwise use recalculated changes
    const changesToUse = capturedQuotesChangesRef.current || currentChanges;

    handleIncrementalUpdates<string>(
      latestIsFeedOpen,
      latestHasInitialized,
      changesToUse,
      latestSubscribedQuotes, // Pass current subscribed quotes
      (items) => getFeedSubscription({ quotes: items }),
      (updatedQuotes) => {
        // With pagination-based filtering, subscriptions are managed per page
        // The unsubscribe logic in handleIncrementalUpdates already handles removals correctly
        // Just set the updated quotes - no need for complex sync logic
        setSubscribedQuotes(updatedQuotes);
      },
      'quotes'
    );

    // Clear captured changes after use
    capturedQuotesChangesRef.current = null;
  }, [handleIncrementalUpdates, setSubscribedQuotes]);

  // Update captured changes when quotesChangesKey changes (when new changes are detected)
  useEffect(() => {
    if (quotesChangesKey && quotesChanges.toAdd.length > 0 || quotesChanges.toRemove.length > 0) {
      capturedQuotesChangesRef.current = {
        toAdd: [...quotesChanges.toAdd],
        toRemove: [...quotesChanges.toRemove],
      };
    }
  }, [quotesChangesKey, quotesChanges]);

  useDebouncedSubscription(
    isFeedOpen,
    hasInitialized, // Use state directly - no need for ref
    quotesChanges,
    quotesHandler,
    quotesChangesKey // Pass stable key to prevent unnecessary debounce resets
  );

  // ============================================================================
  // Incremental Spread Updates (debounced)
  // ============================================================================
  // Use refs to track latest values so handler always uses current state
  const spreadsChangesRef = useRef(spreadsChanges);
  const subscribedSpreadsRef = useRef(subscribedSpreads);
  const spreadsRef = useRef(spreads);
  const isSpreadsFeedOpenRef = useRef(isSpreadsFeedOpen);

  // Keep refs up to date
  spreadsChangesRef.current = spreadsChanges;
  subscribedSpreadsRef.current = subscribedSpreads;
  spreadsRef.current = spreads;
  isSpreadsFeedOpenRef.current = isSpreadsFeedOpen;

  // Create handler that uses latest values at handler time
  const spreadsHandler = useCallback(() => {
    // Get latest values at handler time
    const latestSpreads = spreadsRef.current || [];
    const latestSubscribedSpreads = subscribedSpreadsRef.current || [];
    const latestIsSpreadsFeedOpen = isSpreadsFeedOpenRef.current;
    const latestHasSpreadsInitialized = hasSpreadsInitializedRef.current;

    // Recalculate changes at handler time to ensure consistency
    const currentChanges = calculateSpreadChanges(latestSpreads, latestSubscribedSpreads, spreadEquals);

    handleIncrementalUpdates<IFeedSpread>(
      latestIsSpreadsFeedOpen,
      latestHasSpreadsInitialized,
      currentChanges,
      latestSubscribedSpreads,
      (items) => ({ kind: 'Spread', spreadDefinition: items }),
      (updatedSpreads) => {
        setSubscribedSpreads(updatedSpreads);
      },
      'spreads',
      spreadEquals
    );
  }, [handleIncrementalUpdates, setSubscribedSpreads]);

  useDebouncedSubscription(
    isSpreadsFeedOpen,
    hasSpreadsInitializedRef.current,
    spreadsChanges,
    spreadsHandler,
    spreadsChangesKey // Pass stable key to prevent unnecessary debounce resets
  );

  return <></>;
};

// Re-export for backward compatibility
export { getFeedSubscription } from './utils/subscriptionHelpers';
